// Old account approvals: a link that would bring over more than the review threshold waits here,
// with nothing credited, until a person approves it (the full amount is credited once) or rejects it
// (nothing is credited). Either decision is final. Temporary: listed in the nav while anything waits
// or until 30 days after linking closes.
import { useState, type FormEvent, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { BadgeCheck, History, TriangleAlert } from 'lucide-react';
import { api } from '../lib/api';
import { useCan } from '../lib/staff';
import { OldAccountLoader, OldPendingLine, type OldPending } from '../components/OldAccount';
import {
  actorLabel,
  confirmAction,
  CopyButton,
  EmptyState,
  ErrorNote,
  fmtNum,
  fmtUsd,
  KV,
  PageHeader,
  Pill,
  RiskMeter,
  run,
  SeverityPill,
  SkeletonRows,
  StatusPill,
  Tabs,
  TimeAgo,
  UserChip,
  ViewOnly,
} from '../components/ui';

interface Other {
  userId: string;
  username: string;
}
interface Item {
  legacyUserId: string;
  old: {
    username: string | null;
    createdAt: number | null;
    state: string | null;
    lastEarningAt: number | null;
    lastWithdrawalAt: number | null;
    lifetimeEarnedCoins: number;
    lifetimeWithdrawnCoins: number;
    earningsCount: number;
    withdrawalsCount: number;
    reversalsCount: number;
    balanceCoins: number;
  };
  coins: number;
  claimStatus: 'pending_review' | 'approved' | 'rejected';
  claimedAt: number;
  decidedAt: number | null;
  decidedBy: string | null;
  decisionNote: string | null;
  userReason: string | null;
  user: {
    id: string;
    username: string;
    createdAt: number;
    status: string;
    country: string | null;
    riskScore: number;
  };
  flags: { code: string; severity: string; status: string; createdAt: number }[];
  devices: number;
  sharedDevice: Other[];
  sharedIp: Other[];
  note: string | null;
  oldPending: OldPending | null;
}
interface Data {
  section: {
    visible: boolean;
    open: boolean;
    closesAt: number;
    staffUntil: number;
    reviewOverCoins: number;
  };
  counts: {
    pending: number;
    approved: number;
    rejected: number;
    credited: number;
    noCoins: number;
  };
  rejectReasons: Record<string, string>;
  staffNames: Record<string, string>;
  items: Item[];
}

const TABS = [
  { key: 'pending', label: 'Waiting' },
  { key: 'decided', label: 'Decided' },
] as const;
type View = (typeof TABS)[number]['key'];

const day = (t: number | null | undefined) =>
  t
    ? new Date(t).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    : '—';
const nepalDay = (t: number) =>
  new Date(t).toLocaleDateString('en-GB', {
    timeZone: 'Asia/Kathmandu',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

/** A date with how long ago it was, for "is this account still alive?" questions. */
function Dated({ t }: { t: number | null }) {
  if (!t) return <span className="text-stone">Never</span>;
  const days = Math.floor((Date.now() - t) / 86_400_000);
  const ago = days < 1 ? 'today' : days === 1 ? '1 day ago' : `${fmtNum(days)} days ago`;
  return (
    <span className="whitespace-nowrap">
      {day(t)} <span className="font-normal text-stone">({ago})</span>
    </span>
  );
}

/** An id that wraps on a phone instead of running out of the card, with a copy button. */
function Id({ id }: { id: string }) {
  return (
    <span className="inline-flex max-w-full items-center gap-0.5 align-middle">
      <code className="min-w-0 break-all rounded-[6px] bg-canvas px-1.5 py-0.5 text-[12px] text-graphite">
        {id}
      </code>
      <CopyButton text={id} />
    </span>
  );
}

const OLD_STATE: Record<string, string> = {
  active: 'Active',
  deleted: 'Deleted',
  banned: 'Banned',
  blocked: 'Blocked',
};

export function LegacyApprovals() {
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const view = (TABS.find((t) => t.key === params.get('view'))?.key ?? 'pending') as View;
  const { data, error, isLoading, refetch } = useQuery({
    queryKey: ['legacy-approvals', view],
    queryFn: () => api<Data>(`/legacy/approvals?view=${view}`),
    refetchInterval: 30_000,
  });
  const items = data?.items ?? [];
  const s = data?.section;

  return (
    <div className="space-y-5">
      <PageHeader
        title="Old account approvals"
        description={
          s ? (
            <>
              Links that would bring over more than{' '}
              <b className="tabular-nums">{fmtNum(s.reviewOverCoins)} coins</b> wait here, and
              nothing is credited until someone decides. Approving credits the full amount once;
              rejecting credits nothing. <b>Either decision is final</b>: the old account can’t be
              linked again, by this user or anyone else.{' '}
              {Number.isFinite(s.closesAt) && (
                <span className="text-stone">
                  Linking {s.open ? 'closes' : 'closed'} at the end of {nepalDay(s.closesAt)} (Nepal
                  time); this page goes away on {day(s.staffUntil)} once nothing is waiting.
                </span>
              )}
            </>
          ) : (
            'Old-site balances waiting for a person to approve or reject.'
          )
        }
        actions={
          <Link to="/legacy" className="btn-ghost h-10 px-4 py-0 text-[14px]">
            <History size={15} aria-hidden="true" /> All linked old accounts
          </Link>
        }
      />
      <Tabs
        label="Approval status"
        value={view}
        onChange={(k) =>
          setParams((p) => (k === 'pending' ? p.delete('view') : p.set('view', k), p), {
            replace: true,
          })
        }
        items={TABS.map((t) => ({
          key: t.key,
          label: t.label,
          count: data
            ? t.key === 'pending'
              ? data.counts.pending
              : data.counts.approved + data.counts.rejected
            : undefined,
          hot: t.key === 'pending',
        }))}
      />
      <ErrorNote error={error} onRetry={() => void refetch()} />
      {isLoading ? (
        <div className="card overflow-hidden">
          <SkeletonRows rows={3} />
        </div>
      ) : items.length === 0 && !(error && !data) ? (
        <div className="card">
          <EmptyState
            icon={BadgeCheck}
            title={view === 'pending' ? 'Nothing waiting' : 'Nothing decided yet'}
          >
            {view === 'pending'
              ? `Links over ${fmtNum(s?.reviewOverCoins ?? 2000)} coins land here for a person to approve or reject. Smaller ones are credited at once.`
              : 'Approved and rejected links show here with who decided and why.'}
          </EmptyState>
        </div>
      ) : (
        <ul className="space-y-4">
          {items.map((it) => (
            <ApprovalCard
              key={it.legacyUserId}
              it={it}
              reasons={data!.rejectReasons}
              staffNames={data!.staffNames}
              onDone={() => qc.invalidateQueries()}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function ApprovalCard({
  it,
  reasons,
  staffNames,
  onDone,
}: {
  it: Item;
  reasons: Record<string, string>;
  staffNames: Record<string, string>;
  onDone: () => Promise<void>;
}) {
  const [rejecting, setRejecting] = useState(false);
  const canDecide = useCan()('legacy.decide');
  const pending = it.claimStatus === 'pending_review';
  const who = it.user.username;

  async function approve() {
    const note = await confirmAction({
      title: `Approve ${fmtNum(it.coins)} coins for ${who}?`,
      body: `They’re credited to ${who}’s balance right away (${fmtUsd(it.coins / 1000)}) and ${who} gets an email.${
        it.oldPending?.waiting
          ? ` The ${fmtNum(it.oldPending.waiting)} coins still pending on the old site come over too, held until their release dates and checked against the old site first.`
          : ''
      } This is final: it can’t be undone or rejected later.`,
      confirmLabel: `Approve and credit ${fmtNum(it.coins)} coins`,
      tone: 'coin',
      reason: {
        label: 'Note for the audit log',
        presets: [
          'Old-site history checks out',
          'Reversals were few and old',
          'Checked with the user',
        ],
      },
    });
    if (note === null) return;
    await run(
      () => api(`/legacy/${encodeURIComponent(it.legacyUserId)}/approve`, { body: { note } }),
      `Approved. ${fmtNum(it.coins)} coins credited to ${who}.`,
    );
    await onDone();
  }

  const others = (list: Other[]) =>
    list.slice(0, 5).map((o, i) => (
      <span key={o.userId}>
        {i > 0 && ', '}
        <Link
          to={`/users/${o.userId}`}
          className="font-semibold underline decoration-ink/25 underline-offset-2 hover:decoration-ink"
        >
          {o.username}
        </Link>
      </span>
    ));

  return (
    <li className="card overflow-hidden">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3.5 sm:px-5">
        <div className="min-w-0 flex-1">
          <UserChip
            id={it.user.id}
            username={it.user.username}
            deleted={it.user.status === 'deleted'}
            sub={`Old site: ${it.old.username ?? it.legacyUserId}`}
          />
        </div>
        <div className="text-right tabular-nums">
          <div className="text-[22px] font-extrabold leading-tight text-coin-ink">
            {fmtNum(it.coins)} coins
          </div>
          <div className="text-[12px] text-stone">
            {fmtUsd(it.coins / 1000)} · linked <TimeAgo t={it.claimedAt} />
          </div>
        </div>
      </div>
      {it.oldPending && (
        <div className="px-4 pb-3 sm:px-5">
          <OldPendingLine p={it.oldPending} />
        </div>
      )}

      {it.note && (
        <div
          role="note"
          className="flex items-start gap-2 border-y border-sun/40 bg-sun/15 px-4 py-2.5 text-[13px] leading-snug sm:px-5"
        >
          <TriangleAlert size={15} className="mt-px shrink-0" aria-hidden="true" />
          <span className="min-w-0">
            <b className="font-semibold">{it.note}</b>
            {it.sharedDevice.length > 0 && <> Device: {others(it.sharedDevice)}.</>}
            {it.sharedIp.length > 0 && <> IP: {others(it.sharedIp)}.</>}
          </span>
        </div>
      )}

      <div className="grid gap-x-8 gap-y-5 border-t border-hairline px-4 py-4 sm:px-5 md:grid-cols-2">
        <section aria-label="Old account">
          <h3 className="mb-2.5 text-[13px] font-semibold text-stone">Old account</h3>
          <KV
            items={[
              ['Username', it.old.username ?? '—'],
              ['Created', day(it.old.createdAt)],
              ['Last earning', <Dated key="e" t={it.old.lastEarningAt} />],
              ['Last withdrawal', <Dated key="w" t={it.old.lastWithdrawalAt} />],
              ['Earned in total', `${fmtNum(it.old.lifetimeEarnedCoins)} coins`],
              ['Withdrawn in total', `${fmtNum(it.old.lifetimeWithdrawnCoins)} coins`],
              [
                'Activity',
                `${fmtNum(it.old.earningsCount)} credits · ${fmtNum(it.old.withdrawalsCount)} cash-outs`,
              ],
              [
                'Reversals',
                it.old.reversalsCount > 0 ? (
                  <b className="text-berry-deep">{fmtNum(it.old.reversalsCount)}</b>
                ) : (
                  '0'
                ),
              ],
              [
                'Computed balance',
                <b key="b" className="tabular-nums">
                  {fmtNum(it.old.balanceCoins)} coins
                </b>,
              ],
              ['State there', OLD_STATE[it.old.state ?? ''] ?? '—'],
            ]}
          />
        </section>
        <section aria-label="New account">
          <h3 className="mb-2.5 text-[13px] font-semibold text-stone">New account</h3>
          <KV
            items={
              [
                ['Id', <Id key="id" id={it.user.id} />],
                ['Signed up', <Dated key="s" t={it.user.createdAt} />],
                ['Country', it.user.country ?? '—'],
                ['Status', <StatusPill key="st" status={it.user.status} />],
                ['Risk score', <RiskMeter key="r" score={it.user.riskScore} />],
                ['Devices', fmtNum(it.devices)],
                [
                  'Risk flags',
                  it.flags.length === 0 ? (
                    'None'
                  ) : (
                    <span key="f" className="flex flex-wrap gap-1.5">
                      {it.flags.slice(0, 6).map((f, i) => (
                        <span key={i} className="inline-flex items-center gap-1">
                          <SeverityPill severity={f.severity} />
                          <span className={f.status === 'open' ? '' : 'text-stone line-through'}>
                            {f.code.replace(/_/g, ' ')}
                          </span>
                        </span>
                      ))}
                    </span>
                  ),
                ],
                [
                  'Shared with others',
                  it.sharedDevice.length || it.sharedIp.length ? (
                    <span key="sh" className="text-berry-deep">
                      {[
                        it.sharedDevice.length ? `device: ${it.sharedDevice.length}` : '',
                        it.sharedIp.length ? `IP: ${it.sharedIp.length}` : '',
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  ) : (
                    'Nothing'
                  ),
                ],
              ] as [string, ReactNode][]
            }
          />
        </section>
      </div>
      <OldAccountLoader legacyUserId={it.legacyUserId} />

      {pending && !canDecide ? (
        <div className="border-t border-hairline bg-[#fbf8f3] px-4 py-3 sm:px-5">
          <ViewOnly>Approving or rejecting old-account balances isn’t part of your roles.</ViewOnly>
        </div>
      ) : pending ? (
        rejecting ? (
          <RejectForm
            it={it}
            reasons={reasons}
            onCancel={() => setRejecting(false)}
            onDone={onDone}
          />
        ) : (
          <div className="flex flex-wrap items-center gap-2 border-t border-hairline bg-[#fbf8f3] px-4 py-3 sm:px-5">
            <button
              type="button"
              className="btn-coin h-10 px-4 py-0 text-[14px]"
              onClick={approve}
              disabled={it.user.status === 'deleted'}
              title={it.user.status === 'deleted' ? 'The new account was deleted' : undefined}
            >
              Approve {fmtNum(it.coins)} coins
            </button>
            <button
              type="button"
              className="btn-ghost h-10 px-4 py-0 text-[14px] text-berry-deep hover:border-berry-deep"
              onClick={() => setRejecting(true)}
            >
              Reject…
            </button>
            <span className="text-[12px] text-stone sm:ml-auto">
              Either decision is final and emails {who}.
            </span>
          </div>
        )
      ) : (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 border-t border-hairline bg-[#fbf8f3] px-4 py-3 text-[13px] sm:px-5">
          <Pill tone={it.claimStatus === 'approved' ? 'mint' : 'berry'}>
            {it.claimStatus === 'approved' ? 'Approved' : 'Rejected'}
          </Pill>
          <span className="text-stone">
            by {it.decidedBy ? actorLabel(it.decidedBy, staffNames) : '—'}{' '}
            <TimeAgo t={it.decidedAt} />
          </span>
          {it.decisionNote && <span className="text-graphite">“{it.decisionNote}”</span>}
          {it.claimStatus === 'rejected' && it.userReason && (
            <span className="basis-full text-stone">
              User reads: “{reasons[it.userReason] ?? it.userReason}”
            </span>
          )}
        </div>
      )}
    </li>
  );
}

function RejectForm({
  it,
  reasons,
  onCancel,
  onDone,
}: {
  it: Item;
  reasons: Record<string, string>;
  onCancel: () => void;
  onDone: () => Promise<void>;
}) {
  const [userReason, setUserReason] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const ok = !!userReason && note.trim().length >= 3;
  const who = it.user.username;
  const id = `reject-${it.legacyUserId}`;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!ok) return;
    const sure = await confirmAction({
      title: `Reject ${who}’s old balance for good?`,
      body: `Nothing is credited, and the old account can never be linked again, by ${who} or anyone else. ${who} gets an email with the message you picked.`,
      confirmLabel: 'Reject for good',
      tone: 'danger',
    });
    if (sure === null) return;
    await run(
      () =>
        api(`/legacy/${encodeURIComponent(it.legacyUserId)}/reject`, {
          body: { reason: note.trim(), userReason },
        }),
      'Rejected. Nothing was credited.',
    );
    await onDone();
  }

  return (
    <form
      onSubmit={submit}
      className="space-y-4 border-t border-hairline bg-[#fbf8f3] px-4 py-4 sm:px-5"
      aria-labelledby={`${id}-title`}
    >
      <h3 id={`${id}-title`} className="text-[15px] font-bold">
        Reject {fmtNum(it.coins)} coins for {who}
      </h3>
      <fieldset>
        <legend className="label">What {who} reads (in the app and the email)</legend>
        <div className="grid gap-1.5">
          {Object.entries(reasons).map(([code, text]) => {
            const on = userReason === code;
            return (
              <label
                key={code}
                className={`flex cursor-pointer items-start gap-2.5 rounded-[10px] border bg-paper px-3 py-2.5 text-[13px] leading-snug transition ${
                  on ? 'border-ink' : 'border-hairline hover:border-ink'
                }`}
              >
                <input
                  type="radio"
                  name={`${id}-user`}
                  value={code}
                  checked={on}
                  onChange={() => setUserReason(code)}
                  className="mt-0.5 accent-ink"
                />
                <span>
                  We couldn’t approve the coins from your old account. {text} This decision is
                  final.
                </span>
              </label>
            );
          })}
        </div>
      </fieldset>
      <div>
        <label className="label" htmlFor={`${id}-note`}>
          Why (staff only: the audit log, never shown to {who})
        </label>
        <textarea
          id={`${id}-note`}
          className="input min-h-[80px] resize-y text-[14px] leading-relaxed"
          placeholder="At least a few words"
          maxLength={300}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </div>
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <button type="button" className="btn-ghost h-10 px-4 py-0 text-[14px]" onClick={onCancel}>
          Cancel
        </button>
        <button
          type="submit"
          className="btn-primary h-10 !bg-berry-deep px-4 py-0 text-[14px] hover:!bg-[#a82015]"
          disabled={!ok}
        >
          Reject for good
        </button>
      </div>
    </form>
  );
}
